import { type AxiosRequestConfig, type AxiosResponse } from "axios"
import { cloneDeep } from "lodash-es"
import { type StatusCodeHandler } from "@/interfaces/http/status-code-handler.interface"
import AxiosWrapperService from "@/services/axios-wrapper-service"
import { useToastStore } from "@/store/toast.store"
import { AlertTypes } from "@/interfaces/component/alert.interface"
import GlobalsService from "@/services/globals-service"
import { isProductionEnvironment } from "@/helper/app/browser-helper"
import { ODataQueryBuilder } from "@/builder/odata-query-builder.ts"
import { HTTPQuery } from "@/builder/http-query.ts"

export enum HTTPMethods {
  GET = 1,
  POST = 2,
  PUT = 3,
  DELETE = 4
}

class HTTPRequest {
  public baseURL: string

  public path: string

  public method: HTTPMethods

  public data: any

  public headers: any

  public query: HTTPQuery

  public includeSession: boolean

  public beforeErrorHandlingCallback?: () => void

  public statusCodeHandlers: Array<StatusCodeHandler>

  public expectedStatusCodes: number[]

  public clone: boolean

  public responseType?: AxiosRequestConfig["responseType"]

  private static _currentApiIndex: number = 0

  private static readonly PRD_API_URLS = [
    "https://api.peanuts.nl",
    "https://api1.peanuts.nl",
    "https://api2.peanuts.nl"
  ]

  private getNextBaseUrl(): string {
    if (!isProductionEnvironment()) {
      return import.meta.env.VITE_API_URL!
    }
    const url = HTTPRequest.PRD_API_URLS[HTTPRequest._currentApiIndex]
    HTTPRequest._currentApiIndex =
      (HTTPRequest._currentApiIndex + 1) % HTTPRequest.PRD_API_URLS.length
    return url
  }

  constructor(method: HTTPMethods, path: string) {
    this.baseURL = this.getNextBaseUrl()
    this.method = method
    this.path = path
    this.query = new HTTPQuery()
    this.includeSession = true
    this.statusCodeHandlers = []
    this.expectedStatusCodes = [200]
    this.clone = true
  }

  public async execute(): Promise<AxiosResponse> {
    try {
      const url = this.constructURL()

      let { data } = this
      if (this.clone) {
        data = cloneDeep(this.data)
      }

      let response = null
      switch (this.method) {
        case HTTPMethods.GET:
          response = await AxiosWrapperService.getInstance()
            .getAxios()
            .get(url, { headers: this.headers, responseType: this.responseType })
          break
        case HTTPMethods.POST:
          response = await AxiosWrapperService.getInstance()
            .getAxios()
            .post(url, data, { headers: this.headers, responseType: this.responseType })
          break
        case HTTPMethods.PUT:
          response = await AxiosWrapperService.getInstance()
            .getAxios()
            .put(url, data, { headers: this.headers, responseType: this.responseType })
          break
        case HTTPMethods.DELETE:
          response = await AxiosWrapperService.getInstance()
            .getAxios()
            .delete(url, { headers: this.headers, responseType: this.responseType })
          break
      }

      // Return response if the status code is expected
      if (this.expectedStatusCodes.includes(response?.status)) {
        return response
      }

      // Error handling
      else if (!this.handleStatusCode(response)) {
        return Promise.reject(new Error(`Something went wrong. Status code: ${response?.status}`))
      }

      // Return the (error) response anyway if all handlers handled correctly.
      return response
    } catch (error: any) {
      if (this.beforeErrorHandlingCallback) {
        this.beforeErrorHandlingCallback()
      }

      // Return response if the status code is expected
      if (this.expectedStatusCodes.includes(error.response?.status)) {
        return error.response
      }

      if (!this.handleStatusCode(error.response)) {
        if (!isProductionEnvironment()) {
          // Show error alert in non-production environments
          const toastStore = useToastStore()
          toastStore.addToast({
            type: AlertTypes.ERROR,
            title: "Serverfout",
            body: String(error),
            lifetime: 5000
          })
        }
        return Promise.reject(error)
      }
      return error.response // Return the (error) response anyway if all handlers handled correctly.
    }
  }

  private handleStatusCode(response: AxiosResponse) {
    if (this.statusCodeHandlers.length === 0) {
      return false // No handlers, so we can't handle the status code.
    }

    let wasHandled = false
    for (const statusCodeHandler of this.statusCodeHandlers) {
      if (statusCodeHandler.statusCode === response?.status) {
        const correctlyHandled = statusCodeHandler.callback(response)
        if (!correctlyHandled) {
          return false
        }
        wasHandled = true
      }
    }
    return wasHandled
  }

  private constructURL(): string {
    if (this.includeSession) {
      const session = GlobalsService.getInstance().getMySessionUUID()
      this.query = this.query.with("session", session)
    }

    return `${this.baseURL}/${this.path}${this.query.toString()}`
  }
}

export class HTTPRequestBuilder {
  private readonly _httpRequest: HTTPRequest

  constructor(method: HTTPMethods, path: string) {
    this._httpRequest = new HTTPRequest(method, path)
  }

  public baseURL(baseURL: string) {
    this._httpRequest.baseURL = baseURL
    return this
  }

  public setData(data: unknown) {
    if (this._httpRequest.method === HTTPMethods.GET) {
      throw Error("Body is not allowed on GET requests")
    }
    this._httpRequest.data = data
    return this
  }

  public setHeaders(headers: unknown) {
    this._httpRequest.headers = headers
    return this
  }

  public setResponseType(responseType: AxiosRequestConfig["responseType"]) {
    this._httpRequest.responseType = responseType
    return this
  }

  public query(
    query: Record<string, string | number | boolean> | ODataQueryBuilder | HTTPQuery | undefined
  ) {
    this._httpRequest.query = HTTPRequestBuilder.toHTTPQuery(query)
    return this
  }

  public setExpectedStatusCode(statusCode: number | number[]) {
    if (typeof statusCode === "number") {
      statusCode = [statusCode]
    }
    this._httpRequest.expectedStatusCodes = statusCode
    return this
  }

  public setbeforeErrorHandlingCallback(callback?: () => void) {
    this._httpRequest.beforeErrorHandlingCallback = callback
    return this
  }

  public withSession(bool: boolean) {
    this._httpRequest.includeSession = bool
    return this
  }

  public addStatusCodeHandlers(statusCodeHandlers?: StatusCodeHandler[]) {
    if (statusCodeHandlers) {
      this._httpRequest.statusCodeHandlers.push(...statusCodeHandlers)
    }
    return this
  }

  public setCloning(bool: boolean) {
    this._httpRequest.clone = bool
    return this
  }

  public anonymizeResultingWebsocketMessages(bool: boolean = true) {
    this._httpRequest.query = this._httpRequest.query.with("anonymize", bool)
    return this
  }

  public build() {
    return this._httpRequest
  }

  /**
   * Serialize a query input to a URL query string **without** the leading `?`.
   * Kept for callers that need the raw string form (e.g. URL-length checks).
   */
  public static toQueryString(
    query: Record<string, string | number | boolean> | ODataQueryBuilder | HTTPQuery | undefined
  ): string {
    const queryString = HTTPRequestBuilder.toHTTPQuery(query).toString()
    return queryString.charAt(0) === "?" ? queryString.substring(1) : queryString
  }

  /**
   * Normalize any of the supported query input shapes into a single
   * {@link HTTPQuery} value object. This is the canonical conversion used
   * internally and is exposed for callers that need to inspect or
   * combine queries before serialization.
   */
  private static toHTTPQuery(
    query: Record<string, string | number | boolean> | ODataQueryBuilder | HTTPQuery | undefined
  ): HTTPQuery {
    if (!query) {
      return new HTTPQuery()
    }
    if (query instanceof HTTPQuery) {
      return query
    }
    if (query instanceof ODataQueryBuilder) {
      return query.build()
    }
    // string or Record<string, string | number | boolean> — both handled by the ctor.
    return new HTTPQuery(query)
  }
}
